import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { csMyPayments } from "../../api/payments.api";
import { csSession } from "../../auth/session";
import styles from "../../styles/payments.module.css";

// statusları AZ dilinə çevirək
function mapStatusAz(st) {
  const s = String(st || "").toUpperCase();
  if (s === "INITIATED") return { text: "Gözləmədə", cls: "pillGray" };
  if (s === "SUCCESS" || s === "PAID") return { text: "Uğurlu", cls: "pillGreen" };
  if (s === "FAILED") return { text: "Uğursuz", cls: "pillRed" };
  return { text: s || "-", cls: "pillGray" };
}

const AZ_MONTHS = [
  "yanvar", "fevral", "mart", "aprel", "may", "iyun",
  "iyul", "avqust", "sentyabr", "oktyabr", "noyabr", "dekabr"
];

function formatAzDateTime(v) {
  const d = parseDateSafe(v);
  if (!d) return "-";

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = AZ_MONTHS[d.getMonth()];
  const yyyy = d.getFullYear();

  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");

  return `${dd} ${mm} ${yyyy}, ${hh}:${mi}:${ss}`;
}

function parseDateSafe(v) {
  if (!v) return null;

  // artıq Date obyektidirsə
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;

  const s = String(v).trim();
  if (!s) return null;

  // 1) ISO (2026-01-29T19:23:14 / 2026-01-29T19:23:14.123 / 2026-01-29T19:23:14+04:00)
  if (s.includes("-")) {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d;
  }

  // 2) "2026 M01 29, 19:23:14" və "2026 M01 29, 19:23:14.123"
  //    və "2026 M01 29 19:23:14" (vergülsüz)
  const m = s.match(
    /^(\d{4})\s+M(\d{2})\s+(\d{2})(?:,)?\s+(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?$/
  );
  if (m) {
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    const hh = Number(m[4]);
    const mm = Number(m[5]);
    const ss = Number(m[6]);
    const ms = m[7] ? Number(String(m[7]).padEnd(3, "0")) : 0;
    const d = new Date(year, month - 1, day, hh, mm, ss, ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  // 3) son çarə: Date parse
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatAmount(p) {
  const amount = p?.amountInt ? (p.amountInt / 100).toFixed(2) : "0.00";
  const cur = p?.currency || "AZN";
  return `${amount} ${cur}`;
}

export default function Payments() {
  const nav = useNavigate();
  const phone = csSession.getPhone();

  const [list, setList] = useState([]);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setMsg("");
    setLoading(true);
    try {
      const res = await csMyPayments(phone); // ApiResponse
      const payload = res?.data?.data ?? res?.data ?? res ?? [];
      setList(Array.isArray(payload) ? payload : []);
    } catch (e) {
      setMsg(e?.response?.data?.message || e?.message || "Məlumatlar yüklənmədi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!phone) {
      nav("/couriersub/login");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone]);

  const hasAny = list?.length > 0;

  // yeni -> köhnə düzülüş
  const items = useMemo(() => list || [], [list]);

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          <button className={styles.backBtn} onClick={() => nav(-1)} type="button">
            ← Geri
          </button>

          <div className={styles.headText}>
            <div className={styles.title}>Ödənişlər</div>
            <div className={styles.subtitle}>
              {loading ? "Yüklənir..." : "Son ödənişləriniz burada görünəcək."}
            </div>
          </div>
        </div>

        {msg && <div className={styles.error}>{msg}</div>}

        {!hasAny ? (
          <div className={styles.empty}>
            <div className={styles.emptyTitle}>Hələ ödəniş yoxdur</div>
            <div className={styles.emptyText}>
              Ödəniş etdikdən sonra məlumatlar bu səhifədə görünəcək.
            </div>
          </div>
        ) : (
          <div className={styles.list}>
            {items.map((p) => {
              const st = mapStatusAz(p?.status);
              const when = p?.paidAt ? formatAzDateTime(p.paidAt) : formatAzDateTime(p?.createdAt);

              return (
                <div key={p.id} className={styles.row}>
                  <div className={styles.rowTop}>
                    <div className={styles.amount}>{formatAmount(p)}</div>
                    <span className={`${styles.pill} ${styles[st.cls]}`}>{st.text}</span>
                  </div>

                  <div className={styles.meta}>
                    <div className={styles.metaLine}>
                      <span className={styles.metaKey}>Tarix:</span>
                      <span className={styles.metaVal}>{when}</span>
                    </div>

                    <div className={styles.metaLine}>
                      <span className={styles.metaKey}>Sifariş ID:</span>
                      <span className={styles.mono}>{p?.providerOrderId || "-"}</span>
                    </div>

                    <div className={styles.metaLine}>
                      <span className={styles.metaKey}>Tranzaksiya:</span>
                      <span className={styles.mono}>{p?.transactionId || "-"}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className={styles.footerHint}>
          Qeyd: “Uğurlu” statusu abunəliyin aktivləşməsi deməkdir.
        </div>
      </div>
    </div>
  );
}

